import React from 'react';
import s from './Pagination.module.scss';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  // 常に表示するページ数（最大5個）
  const maxPagesToShow = 5;
  const pages: (number | string)[] = [];

  // 現在のページを中心に前後2ページ（合計5ページ）を表示
  let start = Math.max(1, currentPage - 2);  // 現在ページの前に2ページ
  let end = Math.min(totalPages, currentPage + 2);  // 現在ページの後に2ページ

  // 最初と最後のページを表示する調整
  if (currentPage <= 3) {
    end = Math.min(totalPages, maxPagesToShow); // 最初の方に近い場合は1〜5ページ
  }
  if (currentPage >= totalPages - 2) {
    start = Math.max(1, totalPages - maxPagesToShow + 1); // 最後の方に近い場合は最後の5ページ
  }

  // 「...」を前後に追加
  if (start > 1) {
    pages.push(1);  // 最初のページ
    if (start > 2) pages.push('...');  // 省略記号
  }

  // 現在のページ範囲内のページ番号を追加
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  // 最後に「...」を追加し、最後のページを表示する
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push('...');
    pages.push(totalPages);  // 最後のページ
  }

  // 常に5ページを超えないように調整
  if (pages.length > maxPagesToShow) {
    pages.splice(5);  // 5個以上のページが表示されないようにする
  }

  return (
    <div className={s.pagination}>
      <button
        className={`${s.pageButton} ${currentPage === 1 ? s.disabled : ''}`}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        &lt;
      </button>

      {pages.map((page, index) => (
        <button
          key={index}
          className={`${s.pageButton} ${page === currentPage ? s.active : ''}`}
          onClick={() => typeof page === 'number' ? handlePageChange(page) : undefined}
          disabled={typeof page === 'string'}  // 「...」はクリックできない
        >
          {typeof page === 'number' ? page : '...'}
        </button>
      ))}

      <button
        className={`${s.pageButton} ${currentPage === totalPages ? s.disabled : ''}`}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        &gt;
      </button>
    </div>
  );
};

export default Pagination;
